import { pickT } from "@repo/shared";
import { ASSET_CLASS_FORECAST_HORIZONS, type AssetClassForecastHorizon } from "@repo/shared/types";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { T } from "./horizon-chart-config-form.i18n";

// QL-313 — the ONE settings form for every component drawn over the 1Y / 5Y /
// 10Y forecast grid: the asset class forecast (QL-315), the volatility chart
// (QL-314) and the equity share chart (QL-313). The block, top to bottom:
//
//   Show Chart      — Switch, only when the caller passes `showChart` (the
//                     asset class forecast alone may hide its chart; the
//                     compliance charts never do, the ceiling line is the point)
//   Time Ranges     — a multi-select row of pills, the same control the widget's
//                     own toggle is. Unpressing the last pill is allowed and
//                     warned about; the dialogs refuse to save an empty selection.
//   Default Time Range — Select over the ENABLED ranges, only when two or more
//   Show table      — Switch, label + description on the left
//
// The three components used to carry three near-identical files (decided
// 2026-09-25 morning, reversed the same afternoon at prepush: three copies in
// one PR is what the shared decision card was extracted for). What differs per
// component is a prop: which chart it is (picks the table label from the
// form's own sidecar), the id prefix, and whether the chart switch exists.
// See the `horizon-chart-settings` skill.

const HORIZON_LABEL_KEY: Record<
  AssetClassForecastHorizon,
  "horizon1Y" | "horizon5Y" | "horizon10Y"
> = {
  "1Y": "horizon1Y",
  "5Y": "horizon5Y",
  "10Y": "horizon10Y",
};

/** The components that mount this form; each picks its own table description. */
type HorizonChart = "asset_class_forecast" | "volatility_chart" | "equity_share_chart";

type HorizonChartConfigFormProps = {
  flowLanguage: string;
  /** Unique per form on screen (`acf`, `vc`, `esc`): the control ids derive from it. */
  idPrefix: string;
  /** Which component this form configures: picks the table description. */
  chart: HorizonChart;
  /**
   * Present only for a component that may hide its chart (the asset class
   * forecast). Adds the Show Chart switch on top and the "chart and table
   * both off" warning at the bottom.
   */
  showChart?: boolean;
  onShowChartChange?: (show: boolean) => void;
  enabledHorizons: AssetClassForecastHorizon[];
  onEnabledHorizonsChange: (horizons: AssetClassForecastHorizon[]) => void;
  defaultHorizon: AssetClassForecastHorizon;
  onDefaultHorizonChange: (horizon: AssetClassForecastHorizon) => void;
  showTable: boolean;
  onShowTableChange: (show: boolean) => void;
  /**
   * The add-component dialog frames each component's settings in a titled card;
   * the post-create settings dialog already provides that chrome and wants the
   * bare controls.
   */
  framed?: boolean;
};

export function HorizonChartConfigForm({
  flowLanguage,
  idPrefix,
  chart,
  showChart,
  onShowChartChange,
  enabledHorizons,
  onEnabledHorizonsChange,
  defaultHorizon,
  onDefaultHorizonChange,
  showTable,
  onShowTableChange,
  framed = true,
}: HorizonChartConfigFormProps) {
  const t = pickT(T, flowLanguage);
  const hasChartSwitch = showChart !== undefined && onShowChartChange !== undefined;
  const tableDescription = t.tableDescription[chart];

  // Keep the canonical order whatever order the pills were pressed in, and
  // move the default onto the first enabled range when its own is unpressed.
  const handleHorizonsChange = (pressed: string[]) => {
    const next = ASSET_CLASS_FORECAST_HORIZONS.filter((h) => pressed.includes(h));
    onEnabledHorizonsChange(next);
    if (next.length > 0 && !next.includes(defaultHorizon)) onDefaultHorizonChange(next[0]!);
  };

  return (
    <div className={framed ? "space-y-4 border rounded-lg p-4 bg-muted/30" : "space-y-4"}>
      {framed && hasChartSwitch && (
        <div className="space-y-2">
          <Label className="text-base">{t.displayOptions}</Label>
          <p className="text-sm text-muted-foreground">{t.displayDesc}</p>
        </div>
      )}

      {hasChartSwitch && (
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor={`${idPrefix}-show-chart`} className="text-base">
              {t.showChart}
            </Label>
            <p className="text-sm text-muted-foreground">{t.showChartDesc}</p>
          </div>
          <Switch
            id={`${idPrefix}-show-chart`}
            checked={showChart}
            onCheckedChange={onShowChartChange}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-base">{t.timeRanges}</Label>
        <p className="text-sm text-muted-foreground">{t.timeRangesDesc}</p>
        <ToggleGroup
          type="multiple"
          value={enabledHorizons}
          onValueChange={handleHorizonsChange}
          className="justify-start"
          aria-label={t.timeRanges}
        >
          {ASSET_CLASS_FORECAST_HORIZONS.map((h) => (
            <ToggleGroupItem
              key={h}
              value={h}
              className="px-3 text-xs"
              aria-label={t[HORIZON_LABEL_KEY[h]]}
            >
              {h}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {enabledHorizons.length === 0 && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
            {t.atLeastOneRange}
          </div>
        )}
      </div>

      {enabledHorizons.length > 1 && (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-default-horizon`} className="text-base">
            {t.defaultHorizon}
          </Label>
          <p className="text-sm text-muted-foreground">{t.defaultHorizonDesc}</p>
          <Select
            value={defaultHorizon}
            onValueChange={(value) => onDefaultHorizonChange(value as AssetClassForecastHorizon)}
          >
            <SelectTrigger id={`${idPrefix}-default-horizon`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {enabledHorizons.map((horizon) => (
                <SelectItem key={horizon} value={horizon}>
                  {t[HORIZON_LABEL_KEY[horizon]]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor={`${idPrefix}-show-table`} className="text-base">
            {t.showTable}
          </Label>
          <p className="text-sm text-muted-foreground">{tableDescription}</p>
        </div>
        <Switch
          id={`${idPrefix}-show-table`}
          checked={showTable}
          onCheckedChange={onShowTableChange}
        />
      </div>

      {hasChartSwitch && !showChart && !showTable && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.atLeastOneOption}
        </div>
      )}
    </div>
  );
}
